/* -----------------------------
    SLIDER COMPONENT
   ----------------------------- */
class BaseSlider extends HTMLElement {
  constructor() {
    super();

    this.onSlideChangeListener = this.onSlideChange.bind(this);

    this.defaultOptions = {
      slidesPerView: "auto",
      spaceBetween: 0,
      navigation: false,
      pagination: false,
      scrollbar: false,
      grabCursor: true,
      watchSlidesProgress: true,
      mousewheel: {
        forceToAxis: true
      },
      on: {
        afterInit: () => {
          this.classList.add(this.modifiers.initialized);
        },
        slideChange: this.onSlideChangeListener
      }
    };

    this.swiperInstance = null;
  }

  connectedCallback() {
    this.addEventListener("slider:init", this.initSlider.bind(this));
    this.addEventListener("slider:update", this.updateSlider.bind(this));
    this.addEventListener("slider:destroy", this.destroySlider.bind(this));

    this.initSlider();
  }

  disconnectedCallback() {
    this.removeEventListener("slider:init", this.initSlider.bind(this));
    this.removeEventListener("slider:update", this.updateSlider.bind(this));
    this.removeEventListener("slider:destroy", this.destroySlider.bind(this));

    this.destroySlider();
  }

  get selectors() {
    return {
      slide: "slider-slide",
      visibleSlide: ".swiper-slide-visible"
    }
  }

  get modifiers() {
    return {
      initialized: "initialized",
      ghost: "ghost",
      observableVisibility: "slider--visibility-observable"
    }
  }

  get attributes() {
    return {
      scrollbar: "scrollbar",
      prevButton: "prev-button",
      nextButton: "next-button",
      freeMode: "freemode",
      centerInsufficientSlides: "center-insufficient-slides"
    }
  }

  get options() {
    const
      scrollbarSelector = this.getAttribute(this.attributes.scrollbar),
      scrollbarElement = scrollbarSelector ? this.querySelector(scrollbarSelector) : null;

    const
      prevButtonSelector = this.getAttribute(this.attributes.prevButton),
      prevButtonElement = prevButtonSelector ? this.querySelector(prevButtonSelector) ?? document.querySelector(prevButtonSelector) : null;

    const
      nextButtonSelector = this.getAttribute(this.attributes.nextButton),
      nextButtonElement = nextButtonSelector ? this.querySelector(nextButtonSelector) ?? document.querySelector(nextButtonSelector) : null;

    const freeMode = this.getAttribute(this.attributes.freeMode) === "true";

    const centerInsufficientSlides = this.getAttribute(this.attributes.centerInsufficientSlides) === "true";

    const customOptions = {
      ...(scrollbarElement && {
        scrollbar: {
          el: scrollbarElement,
          draggable: false
        }
      }),
      ...(prevButtonElement && nextButtonElement && {
        navigation: {
          prevEl: prevButtonElement,
          nextEl: nextButtonElement
        }
      }),
      ...(freeMode && {
        freeMode: true
      }),
      ...(centerInsufficientSlides && {
        centerInsufficientSlides: true
      })
    };

    return {...this.defaultOptions, ...customOptions};
  }

  get slides() {
    return [...this.querySelectorAll(this.selectors.slide)];
  }

  get observableVisibility() {
    return this.classList.contains(this.modifiers.observableVisibility);
  }

  set options(options) {
    this._options = {...this._options, ...options};
  }

  initSlider() {
    if (this.swiperInstance) {
      return;
    }

    this.swiperInstance = new window.Swiper(this, this.options);

    this.updateLastVisibleSlide();
  }

  destroySlider(deleteInstance = true, clearStyles = true) {
    if (!this.swiperInstance) {
      return;
    }

    this.swiperInstance.destroy(deleteInstance, clearStyles);
    this.swiperInstance = null;
  }

  updateSlider() {
    if (!this.swiperInstance) {
      return;
    }

    this.swiperInstance.update();
  }

  onSlideChange() {
    this.updateLastVisibleSlide();
  }

  updateLastVisibleSlide() {
    if (!this.observableVisibility) {
      return;
    }

    const visibleSlides = [...this.querySelectorAll(this.selectors.visibleSlide)];

    if (!visibleSlides.length) {
      return;
    }

    const lastVisibleSlide = visibleSlides[visibleSlides.length - 1];
    const lastSlide = this.slides[this.slides.length - 1];

    this.slides.forEach(slide => slide.classList.remove(this.modifiers.ghost));

    if (lastVisibleSlide.isEqualNode(lastSlide)) {
      lastVisibleSlide.classList.remove(this.modifiers.ghost);
      return;
    }

    lastVisibleSlide.classList.add(this.modifiers.ghost);
  }
}

if (!customElements.get("base-slider")) {
  customElements.define("base-slider", BaseSlider);
}

/* -----------------------------
    SCROLLABLE SLIDER COMPONENT
   ----------------------------- */

class ScrollSlider extends BaseSlider {
  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();

    this.resizeObserver = new ResizeObserver(this.initSlider.bind(this));
    this.resizeObserver.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (!this.resizeObserver) {
      return;
    }

    this.resizeObserver.disconnect();
  }

  get modifiers() {
    return {
      ...super.modifiers,
      bleed: "slider--bleed",
      scroll: "slider--scroll"
    }
  }

  get breakpoints() {
    return {
      desktop: 992,
      mobile: 375
    }
  }

  initSlider() {
    if (window.innerWidth < this.breakpoints.desktop) {
      this.classList.remove(this.modifiers.bleed);
      this.classList.add(this.modifiers.scroll);

      if (this.swiperInstance) {
        this.destroySlider();
      }
    } else {
      this.classList.add(this.modifiers.bleed);
      this.classList.remove(this.modifiers.scroll);

      if (!this.swiperInstance) {
        this.swiperInstance = new window.Swiper(this, this.options);
      }
    }
  }
}

if (!customElements.get("scroll-slider")) {
  customElements.define("scroll-slider", ScrollSlider);
}
